import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function BlogPostPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(undefined);

  useEffect(() => {
    setBlog(undefined);
    api.get(`/blogs/${slug}`).then(r => setBlog(r.data)).catch(() => setBlog(null));
  }, [slug]);

  if (blog === undefined) {
    return <div className="p-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-eagle-gold" /></div>;
  }

  if (blog === null) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center">
        <h1 className="font-display text-3xl font-bold mb-3">Article not found</h1>
        <p className="text-muted-foreground mb-6">This post may have been unpublished or moved.</p>
        <Link to="/" className="inline-flex items-center gap-2 text-eagle-gold font-semibold hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
      </div>
    );
  }

  return (
    <article>
      {blog.cover_image && (
        <div className="relative h-[45vh] min-h-[320px] overflow-hidden">
          <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-3xl mx-auto px-5 lg:px-8 pb-10 w-full">
              <div className="flex flex-wrap gap-2 mb-4">
                {(blog.tags || []).map(t => (
                  <span key={t} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-eagle-gold/20 text-eagle-gold border border-eagle-gold/30 backdrop-blur">{t}</span>
                ))}
              </div>
              <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white max-w-3xl">{blog.title}</h1>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12">
        {!blog.cover_image && (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {(blog.tags || []).map(t => (
                <span key={t} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-eagle-gold/10 text-eagle-gold border border-eagle-gold/30">{t}</span>
              ))}
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight mb-6">{blog.title}</h1>
          </>
        )}

        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-eagle-gold transition-colors mb-8">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to home
        </Link>

        {blog.excerpt && <p className="text-lg text-muted-foreground leading-relaxed mb-8">{blog.excerpt}</p>}

        <div className="text-base leading-[1.8] whitespace-pre-wrap text-foreground/90">
          {blog.content}
        </div>
      </div>
    </article>
  );
}
